import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Table, Button, Form, InputGroup, Nav, Modal, Spinner, Badge } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import { PERMISSION_TREE, treeCodes, sectionCodes, viewCode } from './permissionTree';
import './Settings.css';

// Záložky sekce Nastavení. Další (Role, Uživatelé, Číselné řady…) se přidají sem.
const TABY = [
  { id: 'groups', title: 'Skupiny', icon: 'bi-people' },
  { id: 'membership', title: 'Uživatelé a skupiny', icon: 'bi-diagram-3' },
];

const PRAZDNY_FORMULAR = { name: '', label: '', description: '', sort_order: 0 };

/**
 * Checkbox se třemi stavy. React-Bootstrap `indeterminate` neumí, musí se
 * nastavit na DOM elementu přes ref.
 */
function TriCheck({ id, label, checked, indeterminate = false, onChange, level = 0, bold = false, hint }) {
  return (
    <label className="set-check" htmlFor={id} style={{ paddingLeft: `${level * 1.5}rem` }}>
      <input
        id={id}
        type="checkbox"
        className="form-check-input"
        checked={checked}
        ref={(el) => { if (el) el.indeterminate = !checked && indeterminate; }}
        onChange={onChange}
      />
      <span className={bold ? 'fw-semibold' : ''}>{label}</span>
      {hint && <span className="text-muted small ms-2">{hint}</span>}
    </label>
  );
}

const jmenoUzivatele = (u) => {
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.email;
};

function Settings() {
  const [aktivni, setAktivni] = useState('groups');

  // ── Skupiny ────────────────────────────────────────────────────────
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [chyba, setChyba] = useState('');
  const [hledat, setHledat] = useState('');

  // ── Přehled uživatel → skupiny ─────────────────────────────────────
  const [membership, setMembership] = useState([]);
  const [loadingMembership, setLoadingMembership] = useState(false);
  const [hledatUzivatele, setHledatUzivatele] = useState('');

  // ── Editační modal ─────────────────────────────────────────────────
  const [modal, setModal] = useState(null); // { group|null }
  const [form, setForm] = useState(PRAZDNY_FORMULAR);
  const [vybraniUzivatele, setVybraniUzivatele] = useState(new Set());
  const [pridavani, setPridavani] = useState(false);
  const [filtrClenu, setFiltrClenu] = useState('');
  const [modalTab, setModalTab] = useState('zaklad');
  const [vybraneRole, setVybraneRole] = useState(new Set());

  // Katalog oprávnění z DB — načte se jednou, strom se podle něj filtruje.
  const [katalog, setKatalog] = useState([]);
  const [ukladam, setUkladam] = useState(false);
  const [chybaModalu, setChybaModalu] = useState('');

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    setChyba('');
    try {
      const result = await api.getGroups(hledat);
      setGroups(result.data || []);
    } catch (e) {
      console.error('Failed to load groups:', e);
      setChyba(e.message || 'Načtení skupin se nepodařilo.');
    } finally {
      setLoadingGroups(false);
    }
  }, [hledat]);

  const loadMembership = useCallback(async () => {
    setLoadingMembership(true);
    try {
      const result = await api.getUsersWithGroups();
      setMembership(result.data || []);
    } catch (e) {
      console.error('Failed to load membership:', e);
      showToast(e.message, 'danger');
    } finally {
      setLoadingMembership(false);
    }
  }, []);

  // Debounce hledání ve skupinách.
  useEffect(() => {
    const timer = setTimeout(loadGroups, 300);
    return () => clearTimeout(timer);
  }, [loadGroups]);

  useEffect(() => {
    if (aktivni === 'membership' && membership.length === 0) loadMembership();
  }, [aktivni, membership.length, loadMembership]);

  // Katalog oprávnění stačí načíst jednou na celou sekci.
  useEffect(() => {
    api.getRoles()
      .then((r) => setKatalog(r.data || []))
      .catch((e) => console.error('Failed to load role catalog:', e));
  }, []);

  // ── Modal ──────────────────────────────────────────────────────────
  const otevriModal = async (group) => {
    setChybaModalu('');
    setFiltrClenu('');
    setPridavani(false);
    setModalTab('zaklad');
    setModal({ group: group || null, loading: true });

    // Seznam uživatelů potřebujeme vždycky — bere se ze stejného přehledu
    // jako záložka „Uživatelé a skupiny", ať se nevolá druhý endpoint.
    if (membership.length === 0) await loadMembership();

    if (group) {
      try {
        const detail = (await api.getGroup(group.id)).data;
        setForm({
          name: detail.name || '',
          label: detail.label || '',
          description: detail.description || '',
          sort_order: detail.sort_order ?? 0,
        });
        setVybraniUzivatele(new Set((detail.users || []).map((u) => u.id)));
        setVybraneRole(new Set((detail.roles || []).map((r) => r.code)));
        setModal({ group: detail, loading: false });
      } catch (e) {
        setChybaModalu(e.message || 'Detail skupiny se nepodařilo načíst.');
        setModal({ group, loading: false });
      }
    } else {
      setForm(PRAZDNY_FORMULAR);
      setVybraniUzivatele(new Set());
      setVybraneRole(new Set());
      setModal({ group: null, loading: false });
    }
  };

  const prepniUzivatele = (id) => {
    setVybraniUzivatele((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ulozit = async (e) => {
    e.preventDefault();
    setChybaModalu('');

    if (!form.name.trim()) {
      setChybaModalu('Název skupiny je povinný.');
      return;
    }

    setUkladam(true);
    try {
      const payload = {
        name: form.name.trim(),
        label: form.label.trim(),
        description: form.description.trim(),
        sort_order: Number(form.sort_order) || 0,
        user_ids: [...vybraniUzivatele],
        role_codes: [...vybraneRole],
      };

      if (modal.group) {
        await api.updateGroup(modal.group.id, payload);
        showToast('Skupina byla uložena.');
      } else {
        await api.createGroup(payload);
        showToast('Skupina byla vytvořena.');
      }

      setModal(null);
      loadGroups();
      // Přehled členství je teď neaktuální, načte se znovu při otevření.
      setMembership([]);
    } catch (e) {
      setChybaModalu(e.message || 'Uložení se nepodařilo.');
    } finally {
      setUkladam(false);
    }
  };

  const smazat = async (group) => {
    setChybaModalu('');
    setUkladam(true);
    try {
      await api.deleteGroup(group.id);
      showToast('Skupina byla smazána.');
      setModal(null);
      loadGroups();
      setMembership([]);
    } catch (e) {
      setChybaModalu(e.message || 'Smazání se nepodařilo.');
    } finally {
      setUkladam(false);
    }
  };

  // ── Oprávnění v modalu ─────────────────────────────────────────────
  // Kódy, které katalog v DB skutečně zná. Strom se podle nich filtruje,
  // ať se nedá zaškrtnout role, kterou by backend stejně zahodil.
  const katalogKody = useMemo(() => new Set(katalog.map((r) => r.code)), [katalog]);

  // Role z katalogu, na které se ve stromu zapomnělo. Nespadnou pod stůl,
  // vykreslí se do sekce „Ostatní" na konci.
  const osirele = useMemo(() => {
    const znameKody = new Set(treeCodes());
    return katalog.filter((r) => !znameKody.has(r.code));
  }, [katalog]);

  const znameKody = (codes) => codes.filter((c) => katalogKody.has(c));

  const stavSkupiny = (codes) => {
    const zname = znameKody(codes);
    const zapnute = zname.filter((c) => vybraneRole.has(c)).length;
    return {
      checked: zname.length > 0 && zapnute === zname.length,
      indeterminate: zapnute > 0 && zapnute < zname.length,
      empty: zname.length === 0,
    };
  };

  /** Zapne nebo vypne celou sekci najednou. */
  const prepniSekci = (section) => {
    const codes = znameKody(sectionCodes(section));
    const vse = codes.length > 0 && codes.every((c) => vybraneRole.has(c));
    setVybraneRole((prev) => {
      const next = new Set(prev);
      codes.forEach((c) => (vse ? next.delete(c) : next.add(c)));
      return next;
    });
  };

  /** Přepínač „Úpravy" — zapne/vypne vytváření, editaci i mazání. */
  const prepniPodskupinu = (section, group) => {
    const codes = znameKody(group.children.map((c) => c.code));
    const vse = codes.length > 0 && codes.every((c) => vybraneRole.has(c));
    setVybraneRole((prev) => {
      const next = new Set(prev);
      if (vse) {
        codes.forEach((c) => next.delete(c));
      } else {
        codes.forEach((c) => next.add(c));
        const vc = viewCode(section);
        if (vc && katalogKody.has(vc)) next.add(vc);
      }
      return next;
    });
  };

  /**
   * Jednotlivé oprávnění.
   *
   * Zaškrtnutí čehokoliv zapne i „Zobrazení" sekce — právo mazat fakturu,
   * kterou nesmíš vidět, nedává smysl. Odškrtnutí „Zobrazení" naopak zhasne
   * celou sekci.
   */
  const prepniRoli = (section, code) => {
    setVybraneRole((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
        if (code === viewCode(section)) {
          znameKody(sectionCodes(section)).forEach((c) => next.delete(c));
        }
      } else {
        next.add(code);
        const vc = viewCode(section);
        if (vc && katalogKody.has(vc)) next.add(vc);
      }
      return next;
    });
  };

  // ── Členové skupiny v modalu ───────────────────────────────────────
  // Aktivní nahoře, neaktivní až za nimi — jinak podle loginu.
  const clenove = useMemo(
    () =>
      membership
        .filter((u) => vybraniUzivatele.has(u.id))
        .sort((a, b) => a.is_active === b.is_active
          ? (a.email || '').localeCompare(b.email || '', 'cs')
          : (a.is_active ? -1 : 1)),
    [membership, vybraniUzivatele],
  );

  // Našeptávač „Přidat uživatele" — jen ti, kdo ve skupině ještě nejsou.
  // Bez zadaného hledání ukazujeme jen prvních pár, ať to není zeď textu.
  const MAX_KANDIDATU = 15;
  const kandidatiVsichni = useMemo(() => {
    const q = filtrClenu.trim().toLowerCase();
    return membership
      .filter((u) => !vybraniUzivatele.has(u.id))
      .filter((u) => !q || (u.email || '').toLowerCase().includes(q) || jmenoUzivatele(u).toLowerCase().includes(q))
      .sort((a, b) => a.is_active === b.is_active
        ? (a.email || '').localeCompare(b.email || '', 'cs')
        : (a.is_active ? -1 : 1));
  }, [membership, vybraniUzivatele, filtrClenu]);

  const kandidati = kandidatiVsichni.slice(0, MAX_KANDIDATU);
  const kandidatiSkryto = kandidatiVsichni.length - kandidati.length;

  // ── Záložka Uživatelé a skupiny ────────────────────────────────────
  const membershipFiltrovane = useMemo(() => {
    const q = hledatUzivatele.trim().toLowerCase();
    const filtered = q
      ? membership.filter(
          (u) =>
            jmenoUzivatele(u).toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            u.groups.some((g) => (g.label || g.name).toLowerCase().includes(q)),
        )
      : membership;

    return [...filtered].sort((a, b) => a.is_active === b.is_active
      ? (a.email || '').localeCompare(b.email || '', 'cs')
      : (a.is_active ? -1 : 1));
  }, [membership, hledatUzivatele]);

  // ── Render: záložka Skupiny ────────────────────────────────────────
  const renderGroups = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <InputGroup size="sm" style={{ maxWidth: 320 }}>
          <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control
            placeholder="Hledat skupinu…"
            value={hledat}
            onChange={(e) => setHledat(e.target.value)}
          />
        </InputGroup>
        <Button variant="primary" size="sm" onClick={() => otevriModal(null)}>
          <i className="bi bi-plus-lg me-1"></i>Nová skupina
        </Button>
      </div>

      {chyba && <div className="alert alert-danger">{chyba}</div>}

      {loadingGroups ? (
        <div className="text-center text-muted py-5">
          <Spinner animation="border" size="sm" className="me-2" />Načítám…
        </div>
      ) : (
        <Table hover className="align-middle set-table">
          <thead>
            <tr>
              <th>Název</th>
              <th>Popisek</th>
              <th>Popis</th>
              <th className="text-center">Uživatelů</th>
              <th className="text-center">Oprávnění</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">Žádné skupiny.</td>
              </tr>
            )}
            {groups.map((g) => (
              <tr key={g.id}>
                <td>
                  <code className="set-group-name">{g.name}</code>
                </td>
                <td>{g.label || <span className="text-muted">—</span>}</td>
                <td className="text-muted small">{g.description || '—'}</td>
                <td className="text-center">
                  <Badge bg={g.user_count ? 'primary' : 'light'} text={g.user_count ? undefined : 'secondary'}>
                    {g.user_count}
                  </Badge>
                </td>
                <td className="text-center">
                  {g.role_count
                    ? <Badge bg="success">{g.role_count}</Badge>
                    : <span className="text-muted">—</span>}
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => otevriModal(g)}>
                    <i className="bi bi-pencil"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  // ── Render: záložka Uživatelé a skupiny ────────────────────────────
  const renderMembership = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <InputGroup size="sm" style={{ maxWidth: 360 }}>
          <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control
            placeholder="Hledat uživatele nebo skupinu…"
            value={hledatUzivatele}
            onChange={(e) => setHledatUzivatele(e.target.value)}
          />
        </InputGroup>
        <Button variant="outline-secondary" size="sm" onClick={loadMembership} disabled={loadingMembership}>
          <i className="bi bi-arrow-clockwise me-1"></i>Obnovit
        </Button>
      </div>

      {loadingMembership ? (
        <div className="text-center text-muted py-5">
          <Spinner animation="border" size="sm" className="me-2" />Načítám…
        </div>
      ) : (
        <Table hover className="align-middle set-table">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Login</th>
              <th>Skupiny</th>
            </tr>
          </thead>
          <tbody>
            {membershipFiltrovane.length === 0 && (
              <tr>
                <td colSpan={2} className="text-center text-muted py-4">Nic nenalezeno.</td>
              </tr>
            )}
            {membershipFiltrovane.map((u) => (
              <tr key={u.id} className={u.is_active ? '' : 'set-row-inactive'}>
                <td>
                  <span className="set-login">{u.email}</span>
                  {!u.is_active && <span className="text-muted small ms-2">(neaktivní)</span>}
                </td>
                <td>
                  {u.groups.length === 0
                    ? <span className="text-muted">—</span>
                    : u.groups.map((g) => (
                        <Badge key={g.id} bg="light" text="dark" className="set-group-badge" title={g.name}>
                          {g.label || g.name}
                        </Badge>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  const ZALOZKY = { groups: renderGroups, membership: renderMembership };

  // ═══════════════════════════════════════════════════════════════════
  return (
    <Container fluid className="px-4 settings">
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-12">
              <h4 className="mb-0">
                <i className="bi bi-gear me-2"></i>Nastavení
              </h4>
            </div>
          </div>
        </Container>
      </div>

      <div className="set-tabs-wrapper">
        <Nav variant="tabs" activeKey={aktivni} onSelect={setAktivni} className="set-tabs">
          {TABY.map((t) => (
            <Nav.Item key={t.id}>
              <Nav.Link eventKey={t.id}>
                <i className={`bi ${t.icon} me-1`}></i>{t.title}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
      <div className="set-body">{(ZALOZKY[aktivni] || renderGroups)()}</div>

      {/* Editace / vytvoření skupiny */}
      <Modal show={!!modal} onHide={() => setModal(null)} size="lg" backdrop="static">
        <Form onSubmit={ulozit}>
          <Modal.Header closeButton>
            <Modal.Title>{modal?.group ? 'Upravit skupinu' : 'Nová skupina'}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {chybaModalu && <div className="alert alert-danger">{chybaModalu}</div>}

            {/* Vnitřní záložky — s formulářem, členy i oprávněními najednou
                by modal přetekl. */}
            <Nav variant="tabs" activeKey={modalTab} onSelect={setModalTab} className="set-modal-tabs mb-3">
              <Nav.Item><Nav.Link eventKey="zaklad">Základní údaje</Nav.Link></Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="clenove">Členové <span className="text-muted">({clenove.length})</span></Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="opravneni">Oprávnění <span className="text-muted">({vybraneRole.size})</span></Nav.Link>
              </Nav.Item>
            </Nav>

            <div className={modalTab === 'zaklad' ? '' : 'd-none'}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Identifikátor skupiny</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    disabled={modal?.group?.is_system}
                    placeholder="napr_sprava_faktur"
                  />
                  {!modal?.group?.is_system && (
                    <Form.Text className="text-muted">
                      Písmena, číslice, tečka, pomlčka, podtržítko.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Popisek</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                    placeholder="Název, který uvidí uživatelé"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-9">
                <Form.Group className="mb-3">
                  <Form.Label>Popis</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Pořadí</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                  />
                </Form.Group>
              </div>
            </div>

            </div>

            <div className={modalTab === 'clenove' ? '' : 'd-none'}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Členové skupiny <span className="text-muted fw-normal">({clenove.length})</span></strong>
              <Button
                size="sm"
                variant={pridavani ? 'secondary' : 'outline-primary'}
                type="button"
                onClick={() => { setPridavani((p) => !p); setFiltrClenu(''); }}
              >
                <i className={`bi ${pridavani ? 'bi-x-lg' : 'bi-person-plus'} me-1`}></i>
                {pridavani ? 'Zavřít' : 'Přidat uživatele'}
              </Button>
            </div>

            {/* Přidávání: našeptávač nad uživateli, kteří ve skupině ještě nejsou. */}
            {pridavani && (
              <div className="set-add-box mb-3">
                <InputGroup size="sm" className="mb-2">
                  <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                  <Form.Control
                    autoFocus
                    placeholder="Hledat uživatele podle loginu nebo jména…"
                    value={filtrClenu}
                    onChange={(e) => setFiltrClenu(e.target.value)}
                  />
                </InputGroup>

                <div className="set-add-results">
                  {kandidati.length === 0 ? (
                    <div className="text-muted small py-2 px-1">
                      {filtrClenu.trim() ? 'Nikdo takový nezbývá.' : 'Všichni uživatelé už jsou ve skupině.'}
                    </div>
                  ) : (
                    kandidati.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="set-add-row"
                        onClick={() => prepniUzivatele(u.id)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        <span className="set-login">{u.email}</span>
                        {jmenoUzivatele(u) !== u.email && (
                          <span className="text-muted small ms-2">{jmenoUzivatele(u)}</span>
                        )}
                        {!u.is_active && <span className="text-muted small ms-2">(neaktivní)</span>}
                      </button>
                    ))
                  )}
                  {kandidatiSkryto > 0 && (
                    <div className="text-muted small py-1 px-1">
                      …a další {kandidatiSkryto}. Zpřesni hledání.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="set-member-list">
              {modal?.loading ? (
                <div className="text-center text-muted py-3">
                  <Spinner animation="border" size="sm" className="me-2" />Načítám…
                </div>
              ) : clenove.length === 0 ? (
                <div className="text-center text-muted py-3">Skupina zatím nemá žádné členy.</div>
              ) : (
                clenove.map((u, i) => (
                  <React.Fragment key={u.id}>
                    {/* Neaktivní účty jdou až za aktivní a jsou oddělené. */}
                    {!u.is_active && (i === 0 || clenove[i - 1].is_active) && (
                      <div className="set-member-divider">Neaktivní</div>
                    )}
                    <div className={`set-member-row${u.is_active ? '' : ' set-member-inactive'}`}>
                      <span className="set-login">{u.email}</span>
                      {jmenoUzivatele(u) !== u.email && (
                        <span className="text-muted small ms-2">{jmenoUzivatele(u)}</span>
                      )}
                      <Button
                        size="sm"
                        variant="link"
                        type="button"
                        className="set-member-remove"
                        title="Odebrat ze skupiny"
                        onClick={() => prepniUzivatele(u.id)}
                      >
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>

            </div>

            <div className={modalTab === 'opravneni' ? '' : 'd-none'}>
              {katalog.length === 0 ? (
                <div className="alert alert-warning mb-0">
                  Katalog oprávnění je prázdný. Spusť migraci
                  <code className="mx-1">backend/sql/028-roles-seed.sql</code>
                  a načti stránku znovu.
                </div>
              ) : (
                <div className="set-perm-tree">
                  {PERMISSION_TREE.map((section) => {
                    const stav = stavSkupiny(sectionCodes(section));
                    if (stav.empty) return null;

                    return (
                      <div key={section.key} className="set-perm-section">
                        <TriCheck
                          id={`perm-${section.key}`}
                          label={<><i className={`bi ${section.icon} me-2`}></i>{section.label}</>}
                          checked={stav.checked}
                          indeterminate={stav.indeterminate}
                          onChange={() => prepniSekci(section)}
                          bold
                        />

                        {!section.single && section.items.map((item) => {
                          if (item.code) {
                            if (!katalogKody.has(item.code)) return null;
                            return (
                              <TriCheck
                                key={item.code}
                                id={`perm-${item.code}`}
                                label={item.label}
                                checked={vybraneRole.has(item.code)}
                                onChange={() => prepniRoli(section, item.code)}
                                level={1}
                              />
                            );
                          }

                          const stavPodskupiny = stavSkupiny(item.children.map((c) => c.code));
                          if (stavPodskupiny.empty) return null;

                          return (
                            <React.Fragment key={item.key}>
                              <TriCheck
                                id={`perm-${item.key}`}
                                label={item.label}
                                checked={stavPodskupiny.checked}
                                indeterminate={stavPodskupiny.indeterminate}
                                onChange={() => prepniPodskupinu(section, item)}
                                level={1}
                              />
                              {item.children.map((child) => katalogKody.has(child.code) && (
                                <TriCheck
                                  key={child.code}
                                  id={`perm-${child.code}`}
                                  label={child.label}
                                  checked={vybraneRole.has(child.code)}
                                  onChange={() => prepniRoli(section, child.code)}
                                  level={2}
                                />
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  })}

                  {/* Role, které jsou v katalogu, ale strom o nich neví. */}
                  {osirele.length > 0 && (
                    <div className="set-perm-section">
                      <div className="fw-semibold mb-1">
                        Ostatní
                        <span className="text-muted small ms-2">role, které zatím nemají místo ve stromu</span>
                      </div>
                      {osirele.map((r) => (
                        <TriCheck
                          key={r.code}
                          id={`perm-${r.code}`}
                          label={r.label}
                          hint={r.code}
                          checked={vybraneRole.has(r.code)}
                          onChange={() => setVybraneRole((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.code)) next.delete(r.code);
                            else next.add(r.code);
                            return next;
                          })}
                          level={1}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="justify-content-between">
            <div>
              {modal?.group && !modal.group.is_system && (
                <Button variant="outline-danger" type="button" onClick={() => smazat(modal.group)} disabled={ukladam}>
                  <i className="bi bi-trash me-1"></i>Smazat
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" type="button" onClick={() => setModal(null)}>Zrušit</Button>
              <Button variant="primary" type="submit" disabled={ukladam}>
                {ukladam ? 'Ukládám…' : 'Uložit'}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Settings;
